import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  item: any;
}

const BUCKET = "research-renders";
const SIGNED_URL_TTL = 60 * 60 * 24 * 7; // 7 days

type DebateRoles = {
  bull?: string;
  bear?: string;
  referee?: string;
  summary?: string;
};

function extractRoleText(row: any): string {
  if (!row) return "";
  const p = row.payload_ref ?? {};
  return (
    p.output ?? p.response ?? p.text ?? p.content ?? row.response_excerpt ?? ""
  );
}

export default function ICPackPanel({ item }: Props) {
  const itemId = item?.id as string | undefined;
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const [draft, setDraft] = useState<any | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [blocker, setBlocker] = useState<{ code: string; message: string; next_steps?: string[] } | null>(null);

  async function refreshSignedUrl(path: string) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL);
    if (error) {
      console.warn("Signed URL refresh failed", error);
      return null;
    }
    setSignedUrl(data.signedUrl);
    return data.signedUrl;
  }

  async function hydrate() {
    if (!itemId) return;
    setHydrating(true);
    try {
      const { data, error } = await supabase
        .from("research_output_drafts")
        .select("*")
        .eq("item_id", itemId)
        .eq("output_kind", "ic_pack")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      setDraft(data ?? null);
      const path = (data?.payload as any)?.storage_path as string | undefined;
      if (path) await refreshSignedUrl(path);
    } catch (e: any) {
      console.warn("IC pack hydrate failed", e);
    } finally {
      setHydrating(false);
    }
  }

  useEffect(() => {
    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  async function buildPackInput() {
    // Pull bull / bear / referee + summary from research_ai_call_runs
    const { data: runs } = await supabase
      .from("research_ai_call_runs")
      .select("action, payload_ref, response_excerpt, created_at")
      .eq("item_id", itemId!)
      .in("action", [
        "tier1_compare",
        "tier1_compare_bull",
        "tier1_compare_bear",
        "tier1_compare_referee",
      ])
      .order("created_at", { ascending: false });

    const latestByAction: Record<string, any> = {};
    (runs ?? []).forEach((r: any) => {
      if (!latestByAction[r.action]) latestByAction[r.action] = r;
    });
    const debate: DebateRoles = {
      bull: extractRoleText(latestByAction["tier1_compare_bull"]),
      bear: extractRoleText(latestByAction["tier1_compare_bear"]),
      referee: extractRoleText(latestByAction["tier1_compare_referee"]),
      summary: extractRoleText(latestByAction["tier1_compare"]),
    };

    // Linked sources via research_item_sources
    const { data: itemSources } = await supabase
      .from("research_item_sources")
      .select("source_id, relevance, pinned, notes")
      .eq("item_id", itemId!);

    // Linked capsules via research_item_sources -> research_meeting_capsule_sources
    const sourceIds = (itemSources ?? []).map((s: any) => s.source_id).filter(Boolean);
    let capsuleIds: string[] = [];
    if (sourceIds.length) {
      const { data: caps } = await supabase
        .from("research_meeting_capsule_sources")
        .select("capsule_id, source_id, role")
        .in("source_id", sourceIds);
      capsuleIds = Array.from(new Set((caps ?? []).map((c: any) => c.capsule_id).filter(Boolean)));
    }

    return {
      item_id: itemId,
      title: item.title,
      summary: item.summary,
      house_view: item.ai_house_view_draft,
      risks: item.ai_risks,
      last_tier1_alignment: item.last_tier1_alignment,
      primary_axis: item.primary_axis,
      portfolio_level: item.portfolio_level,
      conviction: item.conviction,
      time_horizon: item.time_horizon,
      debate,
      source_ids: sourceIds,
      capsule_ids: capsuleIds,
    };
  }

  async function persistDraft(payload: any, runId?: string | null) {
    const { data: u } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("research_output_drafts")
      .insert({
        item_id: itemId!,
        output_kind: "ic_pack",
        status: "rendered",
        payload,
        generated_by_run_id: runId ?? null,
        sent_by: u.user?.id ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    setDraft(data);
    if (payload?.storage_path) await refreshSignedUrl(payload.storage_path);
  }

  async function handleRender() {
    if (!itemId) return;
    setLoading(true);
    setBlocker(null);
    try {
      const pack_input = await buildPackInput();
      const { data, error } = await supabase.functions.invoke("research-ic-pack-render", {
        body: { pack_input },
      });

      // supabase-js wraps non-2xx as `error` with the Response in error.context.
      // Read the body so we can surface the function's structured blocker payload.
      let responseBody: any = data ?? null;
      if (error && !responseBody) {
        const ctx = (error as any)?.context;
        if (ctx && typeof ctx.json === "function") {
          try { responseBody = await ctx.json(); } catch { responseBody = null; }
        }
      }
      const code = responseBody?.code ?? responseBody?.error;

      if (code === "adobe_creds_unconfigured" || (error && code)) {
        setBlocker({
          code: code ?? "render_failed",
          message:
            responseBody?.message ??
            (code === "adobe_creds_unconfigured"
              ? "IC pack rendering is unavailable. Adobe Document Services credentials are not configured."
              : `IC pack render failed: ${code}`),
          next_steps: responseBody?.next_steps,
        });
        return;
      }

      if (error) throw error;

      const payload = (data as any)?.payload ?? data;
      if (!payload?.storage_path) {
        throw new Error("Render succeeded but no storage_path returned");
      }
      await persistDraft(payload, (data as any)?.run_id ?? null);
      toast.success("IC pack rendered");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to render IC pack");
    } finally {
      setLoading(false);
    }
  }

  const lastRenderedAt = useMemo(() => {
    if (!draft?.created_at) return null;
    return format(new Date(draft.created_at), "dd/MM/yyyy HH:mm");
  }, [draft]);

  const hasRender = !!draft && !!(draft.payload as any)?.storage_path;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" /> IC Pack
        </CardTitle>
        {hasRender && (
          <Badge variant="outline" className="text-xs">
            Last rendered {lastRenderedAt}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {blocker && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 text-destructive" />
              <div className="space-y-1">
                <div className="font-medium text-destructive">{blocker.code}</div>
                <div className="text-muted-foreground">{blocker.message}</div>
                {blocker.next_steps && blocker.next_steps.length > 0 && (
                  <ul className="list-disc list-inside text-xs text-muted-foreground mt-1">
                    {blocker.next_steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {hydrating ? (
          <p className="text-sm text-muted-foreground">Checking for existing pack…</p>
        ) : hasRender ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild size="sm" disabled={!signedUrl}>
              <a href={signedUrl ?? "#"} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-1" /> Download PDF
              </a>
            </Button>
            <Button size="sm" variant="outline" onClick={handleRender} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Re-render
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              No IC pack rendered yet for this item.
            </p>
            <Button size="sm" onClick={handleRender} disabled={loading || !!blocker}>
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
              Render IC pack
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
